import React from 'react';

import Panel from '../../design-system/Panel';
import Typography from '../../design-system/Typography';
import DescriptionRow from '../../design-system/DescriptionRow';
import { spacing } from '../../design-system/tokens';

export interface OrderSummaryItem {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface OrderSummaryCardProps {
  /** Título do card (ex.: "Resumo do pedido" ou o nome do local). */
  title: string;
  /** Contador opcional exibido em azul ao lado do título (ex.: "1/2" → "[1/2]"). */
  counter?: string;
  /** Quando definido, exibe o lápis de edição no topo-direito. */
  onEdit?: () => void;
  /** Rótulo acessível do botão de edição. @default 'Editar' */
  editLabel?: string;
  /** Itens do resumo, em ordem. */
  items: OrderSummaryItem[];
}

const PencilIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    width="20"
    height="20"
  >
    <path
      d="M14.166 2.5a2.357 2.357 0 0 1 3.334 3.333L6.667 16.667 2.5 17.5l.833-4.167L14.166 2.5z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const editBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: '#0B29FB',
  cursor: 'pointer',
  flexShrink: 0,
};

/**
 * OrderSummaryCard — card de resumo de um pedido/local. Reusado no step
 * "Resumo do pedido" (por local) e no "Resumo geral do pedido" (lista com lápis).
 *
 * Composite de feature: compõe `Panel` + `Typography` + `DescriptionRow` (DS).
 * Quando `onEdit` é fornecido, mostra o lápis no topo-direito (ideia de edição).
 */
export const OrderSummaryCard = ({
  title,
  counter,
  onEdit,
  editLabel = 'Editar',
  items,
}: OrderSummaryCardProps): JSX.Element => (
  <Panel bordered shadow={false} padding="lg">
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spacing.md,
        }}
      >
        <Typography as="p" size="md" weight="bold" color="#3D3D3D">
          {title}
          {counter && (
            <Typography as="span" size="md" weight="bold" color="#0B29FB">
              {` [${counter}]`}
            </Typography>
          )}
        </Typography>

        {onEdit && (
          <button
            type="button"
            style={editBtnStyle}
            aria-label={editLabel}
            onClick={onEdit}
          >
            <PencilIcon />
          </button>
        )}
      </div>

      <div>
        {items.map((it, i) => (
          <DescriptionRow
            // lista estática e ordenada → index é chave estável
            // eslint-disable-next-line react/no-array-index-key
            key={`${title}-${i}`}
            icon={it.icon}
            label={it.label}
            value={it.value}
            divider={i < items.length - 1}
          />
        ))}
      </div>
    </div>
  </Panel>
);

export default OrderSummaryCard;
